import {
	FunctionComponent,
	PointerEvent as ReactPointerEvent,
	useCallback,
	useContext,
	useLayoutEffect,
	useRef,
} from "react";
import { useRespondTo } from "@animech/ui";
import { ViewportPanel } from "../viewport-panel";
import * as Styled from "./layout-root-panel.style";
import {
	useUiData,
	CanvasPanel,
	ConfigurationPanel,
	LoadingPanel,
	OverlayPanel,
	MultiEditPenPanel,
} from "src/ui";
import { ApiDataContext, useSave } from "src/api";
import { useKeyboardShortcuts } from "src/ui/hooks";

const DRAG_DISMISS_THRESHOLD = 80;
const DRAG_TAP_THRESHOLD = 8;

export const LayoutRootPanel: FunctionComponent = () => {
	const rootRef = useRef<HTMLDivElement>(null);
	const { history, view, visualization, loading, logo, renderUiSlot } =
		useUiData();
	const {
		leftSidebarOpen,
		onToggleBottombar,
		multiEditPenPanelOpen,
		dragOperationActive,
	} = visualization;
	const { articles } = useContext(ApiDataContext);
	const saveConfiguration = useSave();
	const { isLoading: isApiLoading } = useContext(ApiDataContext);
	const isMobileBreakpoint = useRespondTo("mobile");
	const isMobile = isMobileBreakpoint && !window.electron;
	const containerRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const dragState = useRef({
		active: false,
		startY: 0,
		pointerId: -1,
	});

	useLayoutEffect(() => {
		if (!isMobile || !contentRef.current || !containerRef.current) {
			return;
		}

		const container = containerRef.current;
		const content = contentRef.current;

		const measure = (el: Element) => {
			const contentHeight = el.getBoundingClientRect().height;
			const dragHandleHeight = 60;
			const cappedHeight = Math.min(
				contentHeight + dragHandleHeight,
				window.innerHeight * 0.75,
			);
			container.style.setProperty("--mobile-panel-height", `${cappedHeight}px`);
		};

		// Synchronous initial measurement before first paint
		measure(content);

		let rafId = 0;
		const observer = new ResizeObserver((entries) => {
			cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(() => {
				measure(entries[0].target);
			});
		});

		observer.observe(content);
		return () => {
			cancelAnimationFrame(rafId);
			observer.disconnect();
		};
	}, [isMobile]);

	const onConfigurationClick = useCallback(() => {
		if (!leftSidebarOpen) {
			onToggleBottombar(true);
		}
	}, [leftSidebarOpen, onToggleBottombar]);

	const onDragPointerDown = useCallback((e: ReactPointerEvent) => {
		e.stopPropagation();
		const handle = e.currentTarget as HTMLElement;
		handle.setPointerCapture(e.pointerId);
		dragState.current = {
			active: true,
			startY: e.clientY,
			pointerId: e.pointerId,
		};
		if (panelRef.current) {
			panelRef.current.style.transition = "none";
		}
		if (containerRef.current) {
			// Set visible height BEFORE activating dragging CSS rules to prevent
			// a one-frame blink where the variable is unset and falls back to 0px
			const panelHeight = panelRef.current?.getBoundingClientRect().height ?? 0;
			containerRef.current.style.setProperty(
				"--mobile-panel-visible-height",
				`${panelHeight}px`,
			);
			containerRef.current.setAttribute("data-panel-dragging", "");
		}
	}, []);

	const onDragPointerMove = useCallback(
		(e: ReactPointerEvent) => {
			if (!dragState.current.active) {
				return;
			}
			const deltaY = e.clientY - dragState.current.startY;
			// Only allow dragging down when open, or up when closed
			const clampedDelta = leftSidebarOpen
				? Math.max(0, deltaY)
				: Math.min(0, deltaY);
			if (panelRef.current) {
				const base = leftSidebarOpen ? 0 : 100;
				panelRef.current.style.transform = `translateY(calc(${base}% + ${clampedDelta}px))`;
			}
			if (containerRef.current) {
				const panelHeight =
					panelRef.current?.getBoundingClientRect().height ?? 0;
				const visibleHeight = leftSidebarOpen
					? panelHeight - clampedDelta
					: Math.abs(clampedDelta);
				containerRef.current.style.setProperty(
					"--mobile-panel-visible-height",
					`${visibleHeight}px`,
				);
			}
		},
		[leftSidebarOpen],
	);

	const onDragPointerUp = useCallback(
		(e: ReactPointerEvent) => {
			if (!dragState.current.active) {
				return;
			}
			const deltaY = e.clientY - dragState.current.startY;
			dragState.current.active = false;

			if (panelRef.current) {
				panelRef.current.style.transition = "";
				panelRef.current.style.transform = "";
			}
			if (containerRef.current) {
				containerRef.current.removeAttribute("data-panel-dragging");
				containerRef.current.style.removeProperty(
					"--mobile-panel-visible-height",
				);
			}

			const absDelta = Math.abs(deltaY);
			if (absDelta < DRAG_TAP_THRESHOLD) {
				// Treat as tap — toggle closed
				onToggleBottombar(false);
			} else if (absDelta > DRAG_DISMISS_THRESHOLD) {
				if (leftSidebarOpen && deltaY > 0) {
					onToggleBottombar(false);
				} else if (!leftSidebarOpen && deltaY < 0) {
					onToggleBottombar(true);
				}
			}
			// else: didn't pass threshold, snaps back via CSS transition
		},
		[leftSidebarOpen, onToggleBottombar],
	);

	const isLoading = isApiLoading || loading.scene || loading.application;
	const canSaveConfiguration = articles.length > 0;

	useKeyboardShortcuts(
		{
			undo: {
				triggers: [{ keyCode: "KeyZ", ctrlOrCmd: true }],
				execute: history.onUndo,
			},
			redo: {
				triggers: [
					{ keyCode: "KeyY", ctrlOrCmd: true },
					{ keyCode: "KeyZ", shift: true, ctrlOrCmd: true },
				],
				execute: history.onRedo,
			},
			open: {
				triggers: [{ keyCode: "KeyO", ctrlOrCmd: true }],
				execute: () => visualization.onToggleOpenDesignModal(true),
			},
			save: {
				triggers: [{ keyCode: "KeyS", ctrlOrCmd: true }],
				execute: () => {
					if (canSaveConfiguration) {
						saveConfiguration();
					}
				},
			},
		},
		{
			targetScope: rootRef,
			ignoreInputElements: true,
		},
	);

	return (
		<Styled.Root ref={rootRef}>
			<Styled.LayoutContainer ref={containerRef}>
				{view === "configurator" && (
					<Styled.LayoutCanvas
						data-panel-open={isMobile && leftSidebarOpen ? "" : undefined}
					>
						<CanvasPanel />
					</Styled.LayoutCanvas>
				)}
				<Styled.LayoutPanel
					ref={panelRef}
					side={isMobile ? "bottom" : "left"}
					open={leftSidebarOpen}
					onClick={onConfigurationClick}
				>
					{isMobile && (
						<Styled.DragHandle
							data-testid="drag-handle"
							onClick={(e) => e.stopPropagation()}
							onPointerDown={onDragPointerDown}
							onPointerMove={onDragPointerMove}
							onPointerUp={onDragPointerUp}
							onPointerCancel={onDragPointerUp}
						/>
					)}
					{isMobile ? (
						<Styled.MobilePanelContent>
							<div ref={contentRef}>{renderUiSlot("mobileConfigurator")}</div>
						</Styled.MobilePanelContent>
					) : (
						<ConfigurationPanel />
					)}
				</Styled.LayoutPanel>
				<Styled.LayoutViewport dragOperationActive={dragOperationActive}>
					<ViewportPanel />
					{view === "configurator" && <Styled.Notifications />}
				</Styled.LayoutViewport>
			</Styled.LayoutContainer>
			{multiEditPenPanelOpen && !isMobile && (
				<Styled.MultiEditPenContainer leftSidebarOpen={leftSidebarOpen}>
					<MultiEditPenPanel />
				</Styled.MultiEditPenContainer>
			)}
			<OverlayPanel />
			{view !== "configurator" && <Styled.Notifications />}
			{isLoading && <LoadingPanel variant="backdrop" />}
			{loading.startup && <LoadingPanel logoSources={logo.overlay} />}
		</Styled.Root>
	);
};
